import { Card, Grid, Typography } from "@mui/material";
import React, { useState, useEffect } from "react";
import { Header } from "./Header";
import { useTranslation } from "react-i18next";

export type Colors = {
    themeName: string;
    backgroundColor: string;
    buttonBackgroundColor: string;
    buttonTextColor: string;
    cardHeaderBackgroundColor: string;
    cardHeaderTextColor: string;
    linkTextColor: string;
    selectedTableRowColor: string;
    infoIconColor: string;
    downloadIconColor: string;
    playIconColor: string;
    helperIconColor: string;
    deleteIconColor: string;
    redirectIconColor: string;
    optimizationIconColor: string;
};

export const defaultColors: Colors = {
    themeName: "COLOR_PICKER_DEFAULT_THEME",
    backgroundColor: "#A3BBC8",
    buttonBackgroundColor: "#436372",
    buttonTextColor: "#ffffff",
    cardHeaderBackgroundColor: "#436372",
    cardHeaderTextColor: "#ffffff",
    linkTextColor: "#436372",
    selectedTableRowColor: "#c6d0d4",
    infoIconColor: "darkcyan",
    downloadIconColor: "green",
    playIconColor: "#FFD700", // Gold
    helperIconColor: "#e6eefa", // Light Steel Blue
    deleteIconColor: "red",
    redirectIconColor: "orange",
    optimizationIconColor: "#4682B4" // Steel Blue
};

const oceanBreezeColors: Colors = {
    themeName: "COLOR_PICKER_OCEAN_BREEZE_THEME",
    backgroundColor: "#D7E8EE", // Light Blue
    buttonBackgroundColor: "#6FA6B8", // Soft Teal
    buttonTextColor: "#ffffff", // White
    cardHeaderBackgroundColor: "#6FA6B8", // Soft Teal
    cardHeaderTextColor: "#ffffff", // White
    linkTextColor: "#6FA6B8", // Light Blue
    selectedTableRowColor: "#dbe5e9", // Light Grayish Blue
    infoIconColor: "#5DA5A8", // Dark Teal
    downloadIconColor: "#4CAF50", // Medium Green
    playIconColor: "#00CED1", // Dark Turquoise
    helperIconColor: "#f0fbff", // Light Blue
    deleteIconColor: "#E57373", // Light Red
    redirectIconColor: "#FBC02D", // Soft Yellow
    optimizationIconColor: "#5B9AA0" // Medium Teal
};

const forestWhisperColors: Colors = {
    themeName: "COLOR_PICKER_FOREST_WHISPER_THEME",
    backgroundColor: "#A9C0A6", // Soft Green
    buttonBackgroundColor: "#4B6F4F", // Dark Green
    buttonTextColor: "#ffffff", // White
    cardHeaderBackgroundColor: "#4B6F4F", // Dark Green
    cardHeaderTextColor: "#ffffff", // White
    linkTextColor: "#4B6F4F", // Soft Green
    selectedTableRowColor: "#C7D4C5", // Light Greenish Gray
    infoIconColor: "#3D7154", // Forest Green
    downloadIconColor: "#6B8E23", // Olive Green
    playIconColor: "#32CD32", // Lime Green
    helperIconColor: "#e8fae8", // Dark Sea Green
    deleteIconColor: "#FF6F61", // Coral
    redirectIconColor: "#FFD700", // Gold
    optimizationIconColor: "#708238" // Olive Drab
};

const sunsetGlowColors: Colors = {
    themeName: "COLOR_PICKER_SUNSET_GLOW_THEME",
    backgroundColor: "#F4E0D9", // Soft Peach
    buttonBackgroundColor: "#D47B66", // Warm Coral
    buttonTextColor: "#ffffff", // White
    cardHeaderBackgroundColor: "#D47B66", // Warm Coral
    cardHeaderTextColor: "#ffffff", // White
    linkTextColor: "#D47B66", // Soft Peach
    selectedTableRowColor: "#E8CCC4", // Pale Coral
    infoIconColor: "#C2644B", // Dark Coral
    downloadIconColor: "#FF8C00", // Dark Orange
    playIconColor: "#FFA07A", // Light Salmon
    helperIconColor: "#fff7e8", // Moccasin
    deleteIconColor: "#DC143C", // Crimson
    redirectIconColor: "#FFA500", // Orange
    optimizationIconColor: "#CD7F32" // Bronze
};

const twilightPurpleColors: Colors = {
    themeName: "COLOR_PICKER_TWILIGHT_PURPLE_THEME",
    backgroundColor: "#D4C1E3", // Light Lavender
    buttonBackgroundColor: "#6A4C93", // Deep Purple
    buttonTextColor: "#ffffff", // White
    cardHeaderBackgroundColor: "#6A4C93", // Deep Purple
    cardHeaderTextColor: "#ffffff", // White
    linkTextColor: "#6A4C93", // Light Lavender
    selectedTableRowColor: "#BFA4D6", // Pale Purple
    infoIconColor: "#8A5BA1", // Dark Purple
    downloadIconColor: "#9370DB", // Medium Purple
    playIconColor: "#DDA0DD", // Plum
    helperIconColor: "#f2f2ff", // Lavender
    deleteIconColor: "#C71585", // Medium Violet Red
    redirectIconColor: "#FFD700", // Gold
    optimizationIconColor: "#7D3C98" // Amethyst Purple
};

const citrusSplashColors: Colors = {
    themeName: "COLOR_PICKER_CITRUS_SPLASH_THEME",
    backgroundColor: "#F7E7A3", // Light Yellow
    buttonBackgroundColor: "#F28C38", // Bright Orange
    buttonTextColor: "#ffffff", // White
    cardHeaderBackgroundColor: "#F28C38", // Bright Orange
    cardHeaderTextColor: "#ffffff", // White
    linkTextColor: "#F28C38", // Bright Orange
    selectedTableRowColor: "#FBE9C3", // Pale Yellow
    infoIconColor: "#FFA500", // Orange
    downloadIconColor: "#FF4500", // Orange Red
    playIconColor: "#FFD700", // Gold
    helperIconColor: "#fff7eb", // Navajo White
    deleteIconColor: "#DC143C", // Crimson
    redirectIconColor: "#FF6347", // Tomato
    optimizationIconColor: "#FFB347" // Pastel Orange
};

const midnightBluesColors: Colors = {
    themeName: "COLOR_PICKER_MIDNIGHT_BLUES_THEME",
    backgroundColor: "#2E3B4E", // Dark Blue
    buttonBackgroundColor: "#495867", // Slate Gray
    buttonTextColor: "#ffffff", // White
    cardHeaderBackgroundColor: "#495867", // Slate Gray
    cardHeaderTextColor: "#ffffff", // White
    linkTextColor: "#495867", // Slate Gray
    selectedTableRowColor: "#d8dbdf", // Steel Blue
    infoIconColor: "#4A637B", // Deep Blue
    downloadIconColor: "#4682B4", // Steel Blue
    playIconColor: "#1E90FF", // Dodger Blue
    helperIconColor: "#ebfdff", // Powder Blue
    deleteIconColor: "#B22222", // Firebrick
    redirectIconColor: "#FFD700", // Gold
    optimizationIconColor: "#1E90FF" // Dodger Blue
};

const emeraldDreamColors: Colors = {
    themeName: "COLOR_PICKER_EMERALD_DREAM_THEME",
    backgroundColor: "#A4D3A2", // Light Green
    buttonBackgroundColor: "#317256", // Deep Green
    buttonTextColor: "#ffffff", // White
    cardHeaderBackgroundColor: "#317256", // Deep Green
    cardHeaderTextColor: "#ffffff", // White
    linkTextColor: "#317256", // Deep Green
    selectedTableRowColor: "#B7E1B3", // Pale Green
    infoIconColor: "#2E8B57", // Sea Green
    downloadIconColor: "#3CB371", // Medium Sea Green
    playIconColor: "#66CDAA", // Medium Aquamarine
    helperIconColor: "#ebfceb", // Dark Sea Green
    deleteIconColor: "#CD5C5C", // Indian Red
    redirectIconColor: "#FFD700", // Gold
    optimizationIconColor: "#66CDAA" // Medium Aquamarine
};

const rubyRedColors: Colors = {
    themeName: "COLOR_PICKER_RUBY_RED_THEME",
    backgroundColor: "#F5B7B1", // Soft Red
    buttonBackgroundColor: "#C0392B", // Ruby Red
    buttonTextColor: "#ffffff", // White
    cardHeaderBackgroundColor: "#C0392B", // Ruby Red
    cardHeaderTextColor: "#ffffff", // White
    linkTextColor: "#C0392B", // Ruby Red
    selectedTableRowColor: "#E6B0AA", // Pale Red
    infoIconColor: "#A93226", // Dark Red
    downloadIconColor: "#FF4500", // Orange Red
    playIconColor: "#DC143C", // Crimson
    helperIconColor: "#ffe6e3", // Salmon
    deleteIconColor: "#B22222", // Firebrick
    redirectIconColor: "#FFD700", // Gold
    optimizationIconColor: "#B03A2E" // Brick Red
};

const skyHighColors: Colors = {
    themeName: "COLOR_PICKER_SKY_HIGH_THEME",
    backgroundColor: "#B3E5FC", // Light Sky Blue
    buttonBackgroundColor: "#0288D1", // Bright Blue
    buttonTextColor: "#ffffff", // White
    cardHeaderBackgroundColor: "#0288D1", // Bright Blue
    cardHeaderTextColor: "#ffffff", // White
    linkTextColor: "#0288D1", // Bright Blue
    selectedTableRowColor: "#BEE3F8", // Soft Blue
    infoIconColor: "#039BE5", // Deep Sky Blue
    downloadIconColor: "#00BFFF", // Deep Sky Blue
    playIconColor: "#87CEFA", // Light Sky Blue
    helperIconColor: "#edfbff", // Light Blue
    deleteIconColor: "#FF6347", // Tomato
    redirectIconColor: "#FFD700", // Gold
    optimizationIconColor: "#87CEEB" // Sky Blue
};

const autumnHarvestColors: Colors = {
    themeName: "COLOR_PICKER_AUTUMN_HARVEST_THEME",
    backgroundColor: "#F4A261", // Light Pumpkin
    buttonBackgroundColor: "#D36F6F", // Warm Red
    buttonTextColor: "#ffffff", // White
    cardHeaderBackgroundColor: "#D36F6F", // Warm Red
    cardHeaderTextColor: "#ffffff", // White
    linkTextColor: "#D36F6F", // Warm Red
    selectedTableRowColor: "#F8D6C1", // Pale Pumpkin
    infoIconColor: "#BF5B5B", // Dark Coral
    downloadIconColor: "#FF6F61", // Coral
    playIconColor: "#FF4500", // Orange Red
    helperIconColor: "#fff7ed", // Bisque
    deleteIconColor: "#B03A2E", // Dark Red
    redirectIconColor: "#F4A261", // Light Pumpkin
    optimizationIconColor: "#D2691E" // Chocolate
};

const coolMintColors: Colors = {
    themeName: "COLOR_PICKER_COOL_MINT_THEME",
    backgroundColor: "#E0F2F1", // Mint Green
    buttonBackgroundColor: "#4DB6AC", // Teal
    buttonTextColor: "#ffffff", // White
    cardHeaderBackgroundColor: "#4DB6AC", // Teal
    cardHeaderTextColor: "#ffffff", // White
    linkTextColor: "#4DB6AC", // Teal
    selectedTableRowColor: "#e9f6f5", // Light Teal
    infoIconColor: "#00796B", // Dark Teal
    downloadIconColor: "#009688", // Medium Teal
    playIconColor: "#00FF7F", // Spring Green
    helperIconColor: "#edfff9", // Medium Aquamarine
    deleteIconColor: "#FF5722", // Deep Orange
    redirectIconColor: "#FF9800", // Orange
    optimizationIconColor: "#00897B" // Dark Teal
};

const springBlossomColors: Colors = {
    themeName: "COLOR_PICKER_SPRING_BLOSSOM_THEME",
    backgroundColor: "#FCE4EC", // Light Pink
    buttonBackgroundColor: "#F48FB1", // Soft Pink
    buttonTextColor: "#ffffff", // White
    cardHeaderBackgroundColor: "#F48FB1", // Soft Pink
    cardHeaderTextColor: "#ffffff", // White
    linkTextColor: "#F48FB1", // Soft Pink
    selectedTableRowColor: "#fdf0f5", // Pale Pink
    infoIconColor: "#D81B60", // Pink Red
    downloadIconColor: "#E91E63", // Pink
    playIconColor: "#FF69B4", // Hot Pink
    helperIconColor: "#ffebee", // Light Pink
    deleteIconColor: "#C2185B", // Dark Pink
    redirectIconColor: "#F06292", // Light Pink
    optimizationIconColor: "#EC407A" // Bright Pink
};

const retroVintageColors: Colors = {
    themeName: "COLOR_PICKER_RETRO_VINTAGE_THEME",
    backgroundColor: "#F5F5DC", // Beige
    buttonBackgroundColor: "#D2B48C", // Tan
    buttonTextColor: "#ffffff", // White
    cardHeaderBackgroundColor: "#D2B48C", // Tan
    cardHeaderTextColor: "#ffffff", // White
    linkTextColor: "#D2B48C", // Tan
    selectedTableRowColor: "#f8f2ed", // Light Tan
    infoIconColor: "#A0522D", // Sienna
    downloadIconColor: "#8B4513", // Saddle Brown
    playIconColor: "#FFD700", // Gold
    helperIconColor: "#fff5e3", // Moccasin
    deleteIconColor: "#A52A2A", // Brown
    redirectIconColor: "#D2691E", // Chocolate
    optimizationIconColor: "#B8860B" // Dark Goldenrod
};

const galacticNightColors: Colors = {
    themeName: "COLOR_PICKER_GALACTIC_NIGHT_THEME",
    backgroundColor: "#1B1B2F", // Dark Blue
    buttonBackgroundColor: "#4E4F6D", // Slate Gray
    buttonTextColor: "#ffffff", // White
    cardHeaderBackgroundColor: "#4E4F6D", // Slate Gray
    cardHeaderTextColor: "#ffffff", // White
    linkTextColor: "#4E4F6D", // Slate Gray
    selectedTableRowColor: "#d2d2e8", // Darker Blue
    infoIconColor: "#6C5B7B", // Lavender Gray
    downloadIconColor: "#9A8C98", // Cool Gray
    playIconColor: "#8A2BE2", // Blue Violet
    helperIconColor: "#f9e3ff", // Medium Orchid
    deleteIconColor: "#F64C72", // Neon Pink
    redirectIconColor: "#FF6F61", // Coral
    optimizationIconColor: "#8A2BE2" // Blue Violet
};

const themes: Colors[] = [
    defaultColors,
    oceanBreezeColors,
    forestWhisperColors,
    sunsetGlowColors,
    twilightPurpleColors,
    citrusSplashColors,
    midnightBluesColors,
    emeraldDreamColors,
    rubyRedColors,
    skyHighColors,
    autumnHarvestColors,
    coolMintColors,
    springBlossomColors,
    retroVintageColors,
    galacticNightColors
];

const ColorPicker: React.FC = () => {
    const { t } = useTranslation();
    const [theme, setTheme] = useState<Colors>(JSON.parse(localStorage.getItem("appColors") as string) || defaultColors);

    useEffect(() => {
        document.body.style.backgroundColor = theme.backgroundColor;
        document.documentElement.style.setProperty('--button-background-color', theme.buttonBackgroundColor);
        document.documentElement.style.setProperty('--button-text-color', theme.buttonTextColor);
        document.documentElement.style.setProperty('--card-header-background-color', theme.cardHeaderBackgroundColor);
        document.documentElement.style.setProperty('--card-header-text-color', theme.cardHeaderTextColor);
        document.documentElement.style.setProperty('--link-text-color', theme.linkTextColor);
        document.documentElement.style.setProperty('--selected-table-row-color', theme.selectedTableRowColor);
        document.documentElement.style.setProperty('--info-icon-color', theme.infoIconColor);
        document.documentElement.style.setProperty('--download-icon-color', theme.downloadIconColor);
        document.documentElement.style.setProperty('--play-icon-color', theme.playIconColor);
        document.documentElement.style.setProperty('--helper-icon-color', theme.helperIconColor);
        document.documentElement.style.setProperty('--delete-icon-color', theme.deleteIconColor);
        document.documentElement.style.setProperty('--redirect-icon-color', theme.redirectIconColor);
        document.documentElement.style.setProperty('--optimization-icon-color', theme.optimizationIconColor);
    }, [theme]);

    const handleThemeChange = (theme: Colors) => {
        setTheme(theme);
        localStorage.setItem("appColors", JSON.stringify(theme));
    };

    return (
        <Card style={{marginTop: '5px'}}>
            <Header title={t('COLOR_PICKER_HEADER_TEXT')} />
            <div style={{ padding: '20px' }}>
                {
                    themes.map((th: Colors, index: number) => {
                        return(
                            <Grid key={index} container sx={{ display: 'flex', alignItems: 'center' }} spacing={1}>
                                <Grid item>
                                    <button
                                        style={{
                                            backgroundColor: th.backgroundColor,
                                            width: "40px",
                                            height: "40px",
                                            border: "none",
                                            margin: "5px",
                                            cursor: "pointer",
                                            borderRadius: "50%",
                                            outline: th.themeName === theme.themeName ? "3px solid #000" : "none",
                                        }}
                                        onClick={() => handleThemeChange(th)}
                                    />
                                </Grid>
                                <Grid item>
                                    <Typography variant="h6">{t(`${th.themeName}`)}</Typography>
                                </Grid>
                            </Grid>
                        )
                    })
                }
            </div>
        </Card>
    );
};

export default ColorPicker;
